import React, {useEffect} from 'react'
import { ICompany } from '../../models/Company'
import {
    ContextualMenuItemType,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IContextualMenuItem,
    IDropdownOption,
    MessageBar, MessageBarType,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Stack,
    TextField
} from '@fluentui/react';
import {toast} from 'react-toastify';
import StorefrontDropdown from '../Storefront/StorefrontDropdown';
import {useDeleteCompanyMutation, useGetCompanyQuery, useGetContactsForCompanyQuery, useGetLocationsForCompanyQuery, usePutCompanyMutation} from '../../store/Api';
import CompanyContactsDisplay from './CompanyContactsDisplay';
import CompanyLocationsDisplay from './CompanyLocationsDisplay';
import StandardPanel from '../Common/StandardPanel';
import SearchFilter from '../Common/SearchFilter';
import ExternalMappingsDialog from './ExternalMappingsDialog';
import CompanyLinkDialog from './CompanyLinkDialog';
import {useCompanyCrmSyncMenuItems} from './useCompanyCrmSyncMenuItems';
import {useGetCompanyLinksQuery, useGetCompanyLinkableFoliosQuery, useLinkCompanyToFolioMutation} from '../../store/endpoints/RecordLinksEndpoints';
import Setting from '../Common/Setting';
import {useUrlTabState} from '../../hooks/useUrlState';

interface ICompanyDetailsModalProps {
    company?: string;
    isOpen: boolean;
    onClose: () => void;
    /** Pass false to dock beside the page without an overlay. */
    isBlocking?: boolean;
}

const CompanyDetailsModal: React.FC<ICompanyDetailsModalProps> = (props) => {
    const [saveCompany] = usePutCompanyMutation();
    const [deleteCompany] = useDeleteCompanyMutation();
    const [storefront, setStorefront] = React.useState<string | undefined>();
    const [saveError, setSaveError] = React.useState<string | undefined>();
    const [saving, setSaving] = React.useState<boolean>(false);
    const [showMappingDialog, setShowMappingDialog] = React.useState<boolean>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState<boolean>(false);
    const [linkFolio, setLinkFolio] = React.useState<{folioId: string; folioName?: string} | undefined>();
    const [panelTab, setPanelTab] = useUrlTabState('details', {param: 'panelTab'});

    const company = useGetCompanyQuery(props.company ?? '', {skip: !props.company || props.company === ''});
    // Counts surfaced in the delete confirmation (deleting a company cascades to its contacts and locations).
    const companyContacts = useGetContactsForCompanyQuery({companyId: props.company ?? ''}, {skip: !props.company});
    const companyLocations = useGetLocationsForCompanyQuery({companyId: props.company ?? ''}, {skip: !props.company});
    const companyLinks = useGetCompanyLinksQuery(props.company ?? '', {skip: !props.company || props.company === ''});
    const linkableFolios = useGetCompanyLinkableFoliosQuery();
    const [linkCompany] = useLinkCompanyToFolioMutation();
    const syncMenuItems = useCompanyCrmSyncMenuItems(props.company);
    const [workingCompany, setWorkingCompany] = React.useState<Partial<ICompany>>({});

    // Seed the working copy once per company. Don't reset on later refetches (e.g. after an instant link
    // PATCH invalidates the cache) so in-flight, unsaved field edits aren't clobbered.
    const loadedCompanyId = React.useRef<string | undefined>();
    useEffect(() => {
        if(company.data && loadedCompanyId.current !== (company.data.id ?? props.company)){
            loadedCompanyId.current = company.data.id ?? props.company;
            setWorkingCompany({...company.data});
        }
    }, [company.data, props.company]);

    useEffect(() => {
        if (!props.isOpen) {
            setWorkingCompany({});
            setSaveError(undefined);
            setSaving(false);
            setShowMappingDialog(false);
            setLinkFolio(undefined);
        }
    }, [props.isOpen]);

    const externalMappings = workingCompany.externalMappings ?? {};
    const setExternalMappings = (mappings: {[p: string]: string}) =>
        setWorkingCompany((current) => ({...current, externalMappings: mappings}));

    const save = () => {
        if(workingCompany?.companyName === undefined || workingCompany?.companyName === ''){
            setSaveError("Company Name is required");
            return;
        }
        setSaveError(undefined);
        setSaving(true);
        const objectName = workingCompany.companyName;
        const toastId = toast.loading(`Saving ${objectName}…`);
        saveCompany({
            ...workingCompany,
            storefront
        }).unwrap().then(() => {
            toast.update(toastId, {type: 'success', render: `${objectName} saved`, isLoading: false, autoClose: 3000});
            setSaving(false);
            props.onClose();
        }).catch(() => {
            toast.update(toastId, {type: 'error', render: `Failed to save ${objectName}`, isLoading: false, autoClose: 4000});
            setSaving(false);
        });
    }

    // "Link with {CRM}" actions for folios this company isn't already mapped to (keyed by the folio id).
    const linkItems: IContextualMenuItem[] = (linkableFolios.data ?? [])
        .filter(f => !(f.folioId in externalMappings))
        .map(f => ({
            key: `link-${f.folioId}`,
            text: `Link with ${f.folioName ?? 'CRM'}`,
            iconProps: {iconName: 'AddLink'},
            onClick: () => { setLinkFolio({folioId: f.folioId, folioName: f.folioName}); }
        }));

    // Delete the company (saved records only). Confirmed via a dialog; the list updates optimistically.
    const onDelete = async () => {
        setShowDeleteConfirm(false);
        if (!props.company) return;
        const name = workingCompany?.companyName || 'company';
        const toastId = toast.loading(`Deleting ${name}…`, {closeButton: false, closeOnClick: false, draggable: false});
        const resolved = {isLoading: false, autoClose: 4000, closeButton: true, closeOnClick: true, draggable: true};
        try {
            const res = await deleteCompany(props.company).unwrap();
            if (res.success === false) throw new Error(res.message);
            toast.update(toastId, {type: 'success', render: `${name} deleted`, ...resolved});
            props.onClose();
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to delete ${name}`, ...resolved});
        }
    };

    // Header three-dots: "Link with {CRM}" for unlinked folios, then "Sync all …" for linked ones, then Delete.
    const deleteMenuItem: IContextualMenuItem[] = props.company ? [{
        key: 'delete-company',
        text: 'Delete Company',
        iconProps: {iconName: 'Delete'},
        onClick: () => { setShowDeleteConfirm(true); }
    }] : [];
    const menuItems: IContextualMenuItem[] = [
        ...linkItems,
        ...(linkItems.length > 0 && syncMenuItems.length > 0
            ? [{key: 'crm-divider', itemType: ContextualMenuItemType.Divider}] : []),
        ...syncMenuItems,
        ...(deleteMenuItem.length > 0 && (linkItems.length > 0 || syncMenuItems.length > 0)
            ? [{key: 'delete-divider', itemType: ContextualMenuItemType.Divider}] : []),
        ...deleteMenuItem
    ];

    // Friendly folio names for the mapping chips (the keys are folio ids).
    const folioNameById: {[id: string]: string} = {};
    (linkableFolios.data ?? []).forEach(f => { folioNameById[f.folioId] = f.folioName ?? f.folioId; });
    (companyLinks.data ?? []).forEach(l => { if (l.folioName) folioNameById[l.folioId] = l.folioName; });

    // Link is persisted on its own (PATCH) so the user doesn't have to save the whole company. A persistent
    // loading toast shows while saving; it resolves to a dismissable success/error toast.
    const onLink = async (externalId: string) => {
        const folio = linkFolio;
        setLinkFolio(undefined);
        if (!folio) return;
        // Reflect locally right away (chip shows; a later full Save keeps it). Keyed by folio id.
        setExternalMappings({...externalMappings, [folio.folioId]: externalId});
        // A brand-new, unsaved company has no id to PATCH yet — it'll persist when the company is saved.
        if (!props.company) return;

        const label = folio.folioName ?? 'CRM';
        const toastId = toast.loading(`Linking to ${label}…`, {closeButton: false, closeOnClick: false, draggable: false});
        const resolved = {isLoading: false, autoClose: 4000, closeButton: true, closeOnClick: true, draggable: true};
        try {
            const res = await linkCompany({companyId: props.company, folioId: folio.folioId, externalId}).unwrap();
            if (res.success) {
                toast.update(toastId, {type: 'success', render: `Linked to ${label}`, ...resolved});
            } else {
                toast.update(toastId, {type: 'error', render: res.message ?? `Failed to link to ${label}`, ...resolved});
                setExternalMappings(externalMappings); // roll back the optimistic chip
            }
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to link to ${label}`, ...resolved});
            setExternalMappings(externalMappings); // roll back the optimistic chip
        }
    };

    const footer = () => (
        <Stack horizontal tokens={{childrenGap: '1em'}}>
            <PrimaryButton onClick={save} disabled={saving} iconProps={{iconName: 'Save'}}>Save</PrimaryButton>
            <DefaultButton onClick={props.onClose}>Close</DefaultButton>
        </Stack>
    );

    return (
        <StandardPanel
            type={PanelType.medium}
            isBlocking={props.isBlocking}
            isOpen={props.isOpen}
            onDismiss={props.onClose}
            headerText={workingCompany?.companyName ?? 'Company Details'}
            isFooterAtBottom={true}
            onRenderFooterContent={footer}
            links={companyLinks.data}
            menuItems={menuItems}
        >
            <CompanyLinkDialog
                folioId={linkFolio?.folioId}
                folioName={linkFolio?.folioName}
                initialSearch={workingCompany?.companyIdentifier || workingCompany?.companyName}
                onClose={() => setLinkFolio(undefined)}
                onLink={onLink}
            />
            <ExternalMappingsDialog
                display={showMappingDialog}
                onClose={() => setShowMappingDialog(false)}
                onAdd={(type, id) => { setExternalMappings({...externalMappings, [type]: id}); setShowMappingDialog(false); }}
            />
            <Dialog
                hidden={!showDeleteConfirm}
                onDismiss={() => setShowDeleteConfirm(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete company',
                    subText: deleteWarning(workingCompany?.companyName, companyContacts.data?.total ?? 0, companyLocations.data?.total ?? 0)
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={onDelete} text='Delete' styles={{root: {backgroundColor: '#db2828', borderColor: '#db2828'}}} />
                    <DefaultButton onClick={() => setShowDeleteConfirm(false)} text='Cancel' />
                </DialogFooter>
            </Dialog>
            {/* Bound to ?panelTab= rather than left uncontrolled: Locations and Contacts hold their
                own fields, and a link to one of those has to be able to select the tab it lives on. */}
            <Pivot
                styles={{itemContainer:{paddingTop:'1em'}}}
                selectedKey={panelTab}
                onLinkClick={(item) => setPanelTab(item?.props.itemKey)}
            >
                <PivotItem itemKey='details' headerText='Details'>
                    {saveError && <MessageBar messageBarType={MessageBarType.error}>{saveError}</MessageBar>}
                    <StorefrontDropdown useId selected={storefront} onChange={setStorefront} />
                    <CompanyDetailsForm
                        company={props.company ?? ''}
                        onChange={(update) => {setWorkingCompany((current) => {return {...current, ...update,
                            // CRM mappings are edited at the modal level — don't let the form's copy clobber them.
                            externalId: current.externalId, externalIdType: current.externalIdType,
                            externalMappings: current.externalMappings}})}}
                    />
                    <h3>CRM Links</h3>
                    <p style={{marginTop: 0, color: '#605e5c'}}>
                        Use the menu in the header to link this company to a CRM, or add a mapping manually below.
                    </p>
                    {Object.keys(externalMappings).length > 0 &&
                        <div style={{display: 'inline-flex', flexWrap: 'wrap'}}>
                            {Object.keys(externalMappings).map((key) => (
                                <div key={key} style={{margin: '5px'}}>
                                    <SearchFilter onDelete={() => {
                                        const next = {...externalMappings};
                                        delete next[key];
                                        setExternalMappings(next);
                                    }}>
                                        <b>{folioNameById[key] ?? key}:</b> {externalMappings[key]}
                                    </SearchFilter>
                                </div>
                            ))}
                        </div>}
                    <DefaultButton text='Add Mapping' iconProps={{iconName: 'Add'}} onClick={() => setShowMappingDialog(true)} />
                </PivotItem>
                <PivotItem itemKey='contacts' headerText='Contacts'>
                    {props.company && <CompanyContactsDisplay companyId={props.company} />}
                </PivotItem>
                <PivotItem itemKey='locations' headerText='Locations'>
                    {props.company && <CompanyLocationsDisplay companyId={props.company} defaultLocationId={workingCompany?.defaultLocationId} />}
                </PivotItem>
            </Pivot>
        </StandardPanel>
    )
}

interface CompanyDetailsFormProps{
    company: string;
    onChange?: (company: Partial<ICompany>) => void;
}
export const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = (props) => {
    const [workingCompany, setWorkingCompany] = React.useState<Partial<ICompany>>();
    const company = useGetCompanyQuery(props.company ?? '', {skip: !props.company || props.company === ''});

    useEffect(() => {
        if(company.data) {
            setWorkingCompany(() => {return{...company.data}});
        }
    }, [company.data]);
    
    const update = (update: Partial<ICompany>) => {
        setWorkingCompany((current) => {
            return {...current, ...update};
        });
        if(props.onChange) {
            props.onChange({...workingCompany, ...update});
        }
    }
    return(
    <React.Fragment>
        <Setting id='contacts.company.companyName'><TextField label='Company Name' required={true} value={workingCompany?.companyName} onChange={(e, v) => update({companyName: v})} /></Setting>
        <Setting id='contacts.company.companyIdentifier'><TextField label='Company Identifier' value={workingCompany?.companyIdentifier} onChange={(e, v) => update({companyIdentifier: v})} /></Setting>
        <Setting id='contacts.company.createDate'><TextField label='Create Date' value={workingCompany?.createDate?.toString()} disabled readOnly /></Setting>
        <Setting id='contacts.company.lastUpdated'><TextField label='Last Updated' value={workingCompany?.lastAccessed?.toString()} disabled readOnly /></Setting>
        <Setting id='contacts.company.website'><TextField label='Website' value={workingCompany?.website} onChange={(e, v) => update({website: v})} /></Setting>
        <h3>Address</h3>
        <Setting id='contacts.company.address1'><TextField label='Address 1' value={workingCompany?.address?.address1} onChange={(e, v) => update({address: {...workingCompany?.address, address1: v}})} /></Setting>
        <Setting id='contacts.company.address2'><TextField label='Address 2' value={workingCompany?.address?.address2} onChange={(e, v) => update({address: {...workingCompany?.address, address2: v}})} /></Setting>
        <Setting id='contacts.company.address3'><TextField label='Address 3' value={workingCompany?.address?.address3} onChange={(e, v) => update({address: {...workingCompany?.address, address3: v}})} /></Setting>
        <Setting id='contacts.company.city'><TextField label='City' value={workingCompany?.address?.city} onChange={(e, v) => update({address: {...workingCompany?.address, city: v}})} /></Setting>
        <Setting id='contacts.company.stateTerritory'><TextField label='State' value={workingCompany?.address?.stateTerritory} onChange={(e, v) => update({address: {...workingCompany?.address, stateTerritory: v}})} /></Setting>
        <Setting id='contacts.company.country'><TextField label='Country' value={workingCompany?.address?.country} onChange={(e, v) => update({address: {...workingCompany?.address, country: v}})} /></Setting>
        <Setting id='contacts.company.postalCode'><TextField label='Postal Code' value={workingCompany?.address?.postalCodeZip} onChange={(e, v) => update({address: {...workingCompany?.address, postalCodeZip: v}})} /></Setting>
        <h3>Procurement Contact</h3>
        <Setting id='contacts.company.contactFirstName'><TextField label='First Name' value={workingCompany?.contact?.firstName} onChange={(e, v) => update({contact: {...workingCompany?.contact, firstName: v}})} /></Setting>
        <Setting id='contacts.company.contactLastName'><TextField label='Last Name' value={workingCompany?.contact?.lastName} onChange={(e, v) => update({contact: {...workingCompany?.contact, lastName: v}})} /></Setting>
        <Setting id='contacts.company.contactEmail'><TextField label='Email' value={workingCompany?.contact?.emailAddress} onChange={(e, v) => update({contact: {...workingCompany?.contact, emailAddress: v}})} /></Setting>
        <Setting id='contacts.company.contactPhone'><TextField label='Phone' value={workingCompany?.contact?.phoneNumber} onChange={(e, v) => update({contact: {...workingCompany?.contact, phoneNumber: v}})} /></Setting>
        <Setting id='contacts.company.primaryContact'><TextField label='Primary Contact' value={workingCompany?.primaryContact} onChange={(e, v) => update({primaryContact: v})} /></Setting>
        <h3>CRM Sync</h3>
        <Setting id='contacts.company.syncOverride'>
        <Dropdown
            label='Sync this company from CRM'
            selectedKey={triStateKey(workingCompany?.syncOverride)}
            options={SYNC_OVERRIDE_OPTIONS}
            onChange={(e, option) => update({syncOverride: triStateValue(option)})}
        />
        </Setting>
        <Setting id='contacts.company.syncContacts'>
        <Dropdown
            label='Sync contacts'
            selectedKey={triStateKey(workingCompany?.syncContacts)}
            options={SYNC_INHERIT_OPTIONS}
            onChange={(e, option) => update({syncContacts: triStateValue(option)})}
        />
        </Setting>
        <Setting id='contacts.company.syncLocations'>
        <Dropdown
            label='Sync locations'
            selectedKey={triStateKey(workingCompany?.syncLocations)}
            options={SYNC_INHERIT_OPTIONS}
            onChange={(e, option) => update({syncLocations: triStateValue(option)})}
        />
        </Setting>
    </React.Fragment>)
}

/** Confirmation prose for deleting a company, naming how many contacts/locations cascade with it. */
const deleteWarning = (name: string | undefined, contacts: number, locations: number): string => {
    const base = `Permanently delete ${name || 'this company'}? This can't be undone.`;
    const parts = [
        contacts > 0 ? `${contacts} ${contacts === 1 ? 'contact' : 'contacts'}` : undefined,
        locations > 0 ? `${locations} ${locations === 1 ? 'location' : 'locations'}` : undefined
    ].filter(Boolean);
    return parts.length > 0 ? `${base} This will also delete ${parts.join(' and ')}.` : base;
};

/** Tri-state (undefined / true / false) <-> dropdown key helpers for the per-company sync overrides. */
const triStateKey = (v?: boolean): string => v === undefined ? 'inherit' : v ? 'on' : 'off';
const triStateValue = (option?: IDropdownOption): boolean | undefined =>
    option?.key === 'on' ? true : option?.key === 'off' ? false : undefined;
const SYNC_OVERRIDE_OPTIONS: IDropdownOption[] = [
    {key: 'inherit', text: 'Follow CRM default'},
    {key: 'on', text: 'Always sync this company'},
    {key: 'off', text: "Don't sync this company"}
];
const SYNC_INHERIT_OPTIONS: IDropdownOption[] = [
    {key: 'inherit', text: 'Follow CRM default'},
    {key: 'on', text: 'On'},
    {key: 'off', text: 'Off'}
];

export default CompanyDetailsModal